import { HttpErrorResponse } from '@angular/common/http';
import { computed, inject } from '@angular/core';
import { attachmentUploadRejection, type AttachmentUploadRejection } from '@app/api/api.helper';
import { tapResponse } from '@ngrx/operators';
import { patchState, signalStore, withComputed, withMethods, withProps, withState } from '@ngrx/signals';
import { rxMethod } from '@ngrx/signals/rxjs-interop';
import {
  catchError,
  concatMap,
  EMPTY,
  exhaustMap,
  filter,
  finalize,
  map,
  merge,
  of,
  pipe,
  Subject,
  switchMap,
  take,
  takeUntil,
  tap,
  timer,
} from 'rxjs';

import {
  buildCards,
  isCardVisible,
  type BlankDecision,
  type ClauseDecision,
  type DetectionData,
} from '../detection-derivations.helper';
import {
  detectionState,
  type DetectionOutcome,
  type DetectionState,
} from '../detection-progress/detection-state.helper';
import type { ApplyRequest, BuilderStep } from '../models/ai-template-builder.model';
import { signerStatus } from '../step-who-signs/signers.helper';
import { AiTemplateBuilderApiService } from './ai-template-builder-api.service';

const POLL_INTERVAL_MS = 2500;
const POLL_BACKSTOP_MS = 11 * 60 * 1000;
const READY_PAUSE_MS = 1500;
const NO_DATA: DetectionData = {
  document: { paragraphs: [] },
  fields: [],
  clauseSelections: [],
  flaggedValues: [],
  referenceDataBlocks: [],
  notices: [],
};
const TIMED_OUT: DetectionOutcome = { response: { status: 'error', errorCode: 'timeout' } };

/** What the upload dialog hands to the review dialog once detection resolved. */
export interface ReviewHandoff {
  fileName: string | null;
  resourceId: string | null;
  data: DetectionData | null;
}

interface AiTemplateBuilderState {
  step: BuilderStep;
  fileName: string | null;
  resourceId: string | null;
  isUploading: boolean;
  uploadRejection: AttachmentUploadRejection | null;
  detectionState: DetectionState | null;
  scanFinished: boolean;
  data: DetectionData | null;
  isDownloading: boolean;
  downloadFailed: boolean;
  guardianCount: ApplyRequest['eSign']['guardianCount'];
  addMissingFields: boolean;
  noDigitalSignature: boolean;
  blankDecisions: ReadonlyMap<string, BlankDecision>;
  clauseDecisions: ReadonlyMap<string, ClauseDecision>;
}

const initialState: AiTemplateBuilderState = {
  step: 0,
  fileName: null,
  resourceId: null,
  isUploading: false,
  uploadRejection: null,
  detectionState: null,
  scanFinished: false,
  data: null,
  isDownloading: false,
  downloadFailed: false,
  guardianCount: 0,
  addMissingFields: true,
  noDigitalSignature: false,
  blankDecisions: new Map(),
  clauseDecisions: new Map(),
};

function httpStatusOf(error: unknown): number {
  return error instanceof HttpErrorResponse ? error.status : 0;
}

function uploadRejectionOf(error: unknown): AttachmentUploadRejection {
  return error instanceof HttpErrorResponse ? attachmentUploadRejection(error.status, error.error) : 'other';
}

/** A failed status request says nothing about the run, except that access was lost or the run is gone. */
function isRunLost(status: number): boolean {
  return status === 403 || status === 404;
}

function isFinished(outcome: DetectionOutcome): boolean {
  return detectionState(outcome) !== 'pending';
}

/** Asks for the run's status on a fixed beat, one request at a time, until the run is over. */
function pollOutcomes(api: AiTemplateBuilderApiService, resourceId: string) {
  return timer(POLL_INTERVAL_MS, POLL_INTERVAL_MS).pipe(
    exhaustMap(() =>
      api.status(resourceId).pipe(
        map((response): DetectionOutcome | null => ({ response })),
        catchError((error: unknown) => {
          // Not an HTTP failure: the adapter could not read the body, and asking again will not change it.
          if (!(error instanceof HttpErrorResponse)) {
            return of({ httpStatus: 0 });
          }

          return of(isRunLost(error.status) ? { httpStatus: error.status } : null);
        }),
      ),
    ),
    filter((outcome) => outcome !== null),
    filter(isFinished),
  );
}

/** Holds a resolved outcome for a moment, so the ready state shows before the review. */
function pauseWhenResolved(onResolved: () => void) {
  return concatMap((outcome: DetectionOutcome) => {
    if (detectionState(outcome) !== 'resolved') {
      return of(outcome);
    }

    onResolved();

    return timer(READY_PAUSE_MS).pipe(map(() => outcome));
  });
}

/** One builder session. Each builder dialog provides its own, so a new dialog starts clean. */
export const AiTemplateBuilderStore = signalStore(
  withState(initialState),
  withComputed((store) => ({
    dataOrEmpty: computed(() => store.data() ?? NO_DATA),
    /** What apply sends. A template signed on paper needs no signature fields, only a signing area. */
    eSign: computed<ApplyRequest['eSign']>(() =>
      store.noDigitalSignature()
        ? { addMissingFields: false, guardianCount: 0 }
        : { addMissingFields: store.addMissingFields(), guardianCount: store.guardianCount() },
    ),
    uploadFailed: computed(() => store.uploadRejection() !== null),
  })),
  withComputed((store) => ({
    cards: computed(() => buildCards(store.dataOrEmpty())),
  })),
  withComputed((store) => ({
    visibleCards: computed(() => store.cards().filter((card) => isCardVisible(card, store.clauseDecisions()))),
  })),
  /** Ends the requests of the current document, so a late answer cannot land on the next one. */
  withProps(() => ({ _documentChanged: new Subject<void>() })),
  withMethods((store, api = inject(AiTemplateBuilderApiService)) => {
    const settle = (outcome: DetectionOutcome) => {
      const state = detectionState(outcome);
      const data = 'response' in outcome ? (outcome.response.data ?? null) : null;

      patchState(store, {
        detectionState: state,
        scanFinished: false,
        data,
        step: state === 'resolved' ? 1 : 0,
        guardianCount: data ? signerStatus(data.fields).guardians : 0,
        addMissingFields: true,
        noDigitalSignature: false,
      });
    };

    const detection = (resourceId: string) =>
      api.detect(resourceId).pipe(
        map((response): DetectionOutcome => ({ response })),
        catchError((error: unknown) => of({ httpStatus: httpStatusOf(error) })),
        switchMap((outcome) => {
          if (isFinished(outcome)) {
            return of(outcome);
          }

          return merge(pollOutcomes(api, resourceId), timer(POLL_BACKSTOP_MS).pipe(map(() => TIMED_OUT))).pipe(take(1));
        }),
        pauseWhenResolved(() => patchState(store, { scanFinished: true })),
        tap(settle),
      );

    const runDetection = rxMethod<string | null>(
      pipe(
        switchMap((resourceId) => {
          if (!resourceId) {
            return EMPTY;
          }

          patchState(store, {
            resourceId,
            detectionState: 'pending',
            scanFinished: false,
            data: null,
            blankDecisions: initialState.blankDecisions,
            clauseDecisions: initialState.clauseDecisions,
          });

          return detection(resourceId);
        }),
      ),
    );

    return {
      upload: rxMethod<File>(
        pipe(
          exhaustMap((file) => {
            store._documentChanged.next();
            runDetection(null);
            patchState(store, { ...initialState, fileName: file.name, isUploading: true });

            return api.upload(file).pipe(
              tapResponse({
                next: ({ resource_id: resourceId }) => {
                  if (!resourceId) {
                    patchState(store, { isUploading: false, uploadRejection: 'other' });

                    return;
                  }

                  patchState(store, { isUploading: false });
                  runDetection(resourceId);
                },
                error: (error: unknown) =>
                  patchState(store, { isUploading: false, uploadRejection: uploadRejectionOf(error) }),
              }),
              takeUntil(store._documentChanged),
            );
          }),
        ),
      ),

      /** Starts a new run on the file that is already stored. */
      retry(): void {
        runDetection(store.resourceId());
      },

      reset(): void {
        store._documentChanged.next();
        runDetection(null);
        patchState(store, initialState);
      },
    };
  }),
  withMethods((store, api = inject(AiTemplateBuilderApiService)) => ({
    /** Seeds a fresh store with a finished run, for the dialog that picks up the review. */
    startReview(review: ReviewHandoff): void {
      patchState(store, {
        ...initialState,
        ...review,
        detectionState: 'resolved',
        step: 1,
        guardianCount: review.data ? signerStatus(review.data.fields).guardians : 0,
      });
    },

    goToStep(step: BuilderStep): void {
      patchState(store, { step });
    },

    setBlankDecision(entryKey: string, decision: BlankDecision): void {
      patchState(store, { blankDecisions: new Map(store.blankDecisions()).set(entryKey, decision) });
    },

    setGuardianCount(guardianCount: ApplyRequest['eSign']['guardianCount']): void {
      patchState(store, { guardianCount });
    },

    setAddMissingFields(addMissingFields: boolean): void {
      patchState(store, { addMissingFields });
    },

    setNoDigitalSignature(noDigitalSignature: boolean): void {
      patchState(store, { noDigitalSignature });
    },

    /** Saves the current document without review highlights, so the user can fix it in Word and upload it again. */
    download: rxMethod<void>(
      pipe(
        exhaustMap(() => {
          const resourceId = store.resourceId();

          if (!resourceId) {
            return EMPTY;
          }

          patchState(store, { isDownloading: true, downloadFailed: false });

          return api.downloadWithoutHighlights(resourceId, store.fileName() ?? undefined).pipe(
            catchError(() => {
              patchState(store, { downloadFailed: true });

              return EMPTY;
            }),
            takeUntil(store._documentChanged),
            finalize(() => patchState(store, { isDownloading: false })),
          );
        }),
      ),
    ),
  })),
);
